import { useState } from 'react';
import LanguageToggle from '@/components/layout/LanguageToggle';
import LegalSheet from '@/components/layout/LegalSheet';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { scrollToSection, scrollToTop } from '@/lib/scroll';
import { LEGAL_DOCS, LegalDocKey } from '@/lib/legal';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // activeDoc keeps its last value while closed so the slide-out
  // animation doesn't swap the sheet's content mid-exit
  const [legalOpen, setLegalOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState<LegalDocKey>('privacy');

  const openLegalDoc = (doc: LegalDocKey) => {
    setActiveDoc(doc);
    setLegalOpen(true);
  };

  return (
    <footer className="bg-stone-800 text-white">
      <div className="container section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and tagline */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold">
              <span className="text-tifpa-rose">TIFPA</span> Consulting LLC
            </h3>
            <p className="text-sm opacity-80">
              Trusted Tax Solutions for Our Community. Expert financial guidance
              for small businesses and contractors.
            </p>
            <LanguageToggle />
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-medium mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button
                  type="button"
                  onClick={scrollToTop}
                  className="opacity-80 hover:opacity-100 transition-opacity"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => scrollToSection('services')}
                  className="opacity-80 hover:opacity-100 transition-opacity"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => scrollToSection('consultation')}
                  className="opacity-80 hover:opacity-100 transition-opacity"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h4 className="text-lg font-medium mb-4">Business Hours</h4>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-tifpa-rose" />
                <span className="opacity-80">Monday - Sunday: 9 AM–7 PM</span>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-medium mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-tifpa-rose" />
                <a
                  href="tel:(405) 601-6367"
                  className="opacity-80 hover:opacity-100 transition-opacity"
                >
                  (405) 601-6367
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-tifpa-rose" />
                <a
                  href="mailto:tifpaconsulting@gmail.com"
                  className="opacity-80 hover:opacity-100 transition-opacity"
                >
                  tifpaconsulting@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-tifpa-rose mt-1" />
                <a
                  href="https://maps.google.com/?q=3515+S+Pennsylvania+Ave,+Oklahoma+City,+OK+73119"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-80 hover:opacity-100 transition-opacity"
                >
                  3515 S Pennsylvania Ave
                  <br />
                  Oklahoma City, OK 73119
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-white border-opacity-20 mt-12 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm opacity-70">
            © {currentYear} TIFPA Consulting LLC. All rights reserved.
          </p>
          <div className="mt-4 md:mt-0 flex gap-4 text-sm opacity-70">
            <button
              type="button"
              onClick={() => openLegalDoc('privacy')}
              className="hover:opacity-100 transition-opacity"
            >
              Privacy Policy
            </button>
            <button
              type="button"
              onClick={() => openLegalDoc('terms')}
              className="hover:opacity-100 transition-opacity"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>

      <LegalSheet
        open={legalOpen}
        title={LEGAL_DOCS[activeDoc].title}
        content={LEGAL_DOCS[activeDoc].content}
        fullPageHref={LEGAL_DOCS[activeDoc].path}
        onClose={() => setLegalOpen(false)}
      />
    </footer>
  );
};

export default Footer;
